<template>
  <form class="rb-ob-form rb-match-page" novalidate @submit.prevent="submit">
    <header>
      <div class="rb-ob-progress" :aria-label="$t('intake.progressLabel')">
        <div class="rb-ob-progress-meta">
          <span>{{ progressMeta(9) }}</span>
          <span>{{ $t('intake.stepLabels.slots') }}</span>
        </div>
        <div class="rb-ob-progress-bar" aria-hidden="true">
          <i :style="{ '--p': progressPercent(9) }"></i>
        </div>
      </div>
      <h1 class="rb-ob-title">{{ $t('intake.slotsTitle') }}</h1>
      <p class="rb-ob-lead">{{ $t('intake.slotsIntro') }}</p>
    </header>

    <div class="rb-booking-layout">
      <article v-if="person" class="rb-mini-therapist">
        <span class="rb-avatar" aria-hidden="true">{{ person.initial }}</span>
        <div>
          <h2>{{ personName }}</h2>
          <p>
            {{ displaySessionPrice }}
            {{ $t('intake.slotsPriceSuffix') }}
          </p>
        </div>
      </article>

      <div class="rb-booking-fields">
        <div v-if="sandboxMode" class="rb-ob-field-block" :class="{ 'is-invalid': !!errors.date }">
          <p class="rb-ob-q">{{ $t('intake.slotsDateLegend') }}</p>
          <p class="rb-ob-timezone-note">{{ $t('intake.bookingUi.timezone', {zone: timezone}) }}</p>
          <p v-if="slotsLoading" role="status">{{ $t('intake.bookingUi.loading') }}</p>
          <div v-else-if="slotsError" role="alert">
            <p>{{ $t('intake.bookingUi.loadError') }}</p>
            <button type="button" class="rb-ob-submit rb-ob-submit-secondary" @click="ensureSelectionThenLoadSlots(person)">{{ $t('intake.bookingUi.retry') }}</button>
          </div>
          <p v-else-if="!sandboxSlots.length && !rematchNeeded" role="status">{{ $t('intake.bookingUi.empty') }}</p>
          <div class="rb-date-grid">
            <label
                v-for="item in sandboxSlots"
                :key="item.slot_id"
                class="rb-date-card"
                :class="{ 'is-selected': bookingDate === item.slot_id }"
            >
              <input
                  v-model="bookingDate"
                  type="radio"
                  name="bookingDate"
                  :value="item.slot_id"
                  @change="onSandboxSlotChange"
              >
              <span><bdi>{{ item.label }}</bdi></span>
            </label>
          </div>
          <p class="rb-ob-err" :class="{ 'is-visible': !!errors.date }" role="alert">
            {{ errors.date || $t('intake.fieldRequired') }}
          </p>
        </div>

        <template v-else>
          <div class="rb-ob-field-block" :class="{ 'is-invalid': !!errors.date }">
            <p class="rb-ob-q">{{ $t('intake.slotsDateLegend') }}</p>
            <div class="rb-date-grid">
              <label
                  v-for="item in dates"
                  :key="item.id"
                  class="rb-date-card"
                  :class="{ 'is-selected': bookingDate === item.id }"
              >
                <input
                    v-model="bookingDate"
                    type="radio"
                    name="bookingDate"
                    :value="item.id"
                    @change="errors.date = ''"
                >
                <span>{{ dateLabel(item) }}</span>
              </label>
            </div>
            <p class="rb-ob-err" :class="{ 'is-visible': !!errors.date }" role="alert">
              {{ errors.date || $t('intake.fieldRequired') }}
            </p>
          </div>

          <div class="rb-ob-field-block" :class="{ 'is-invalid': !!errors.time }">
            <p class="rb-ob-q">{{ $t('intake.slotsTimeLegend') }}</p>
            <div class="rb-time-grid">
              <label
                  v-for="time in times"
                  :key="time"
                  class="rb-time-card"
                  :class="{ 'is-selected': bookingTime === time }"
              >
                <input
                    v-model="bookingTime"
                    type="radio"
                    name="bookingTime"
                    :value="time"
                    @change="errors.time = ''"
                >
                <span><bdi>{{ toFaDigits(time) }}</bdi></span>
              </label>
            </div>
            <p class="rb-ob-timezone-note">{{ $t('intake.slotsTimezoneNote') }}</p>
            <p class="rb-ob-err" :class="{ 'is-visible': !!errors.time }" role="alert">
              {{ errors.time || $t('intake.fieldRequired') }}
            </p>
          </div>
        </template>

        <div class="rb-ob-actions">
          <button
              class="rb-ob-submit"
              type="submit"
              :disabled="submitting || !!bookingGuard || rematchNeeded || slotsLoading || slotsError || (sandboxMode ? (!priceQuoteId || !isSandboxSlotSelected()) : (!bookingDate || !bookingTime))"
              :class="{ 'is-loading': submitting }"
          >
            {{ $t('intake.slotsContinue') }}
          </button>
          <button
              v-if="rematchNeeded"
              class="rb-ob-submit rb-ob-submit-secondary"
              type="button"
              @click="goRematch"
          >
            {{ $t('intake.rematchAction') }}
          </button>
          <p class="rb-ob-status" role="status" aria-live="polite">{{ formStatus }}</p>
          <router-link v-if="bookingGuard" class="rb-ob-submit rb-ob-submit-secondary" :to="bookingGuard.route">{{ bookingGuard.label }}</router-link>
        </div>
      </div>
    </div>
  </form>
</template>

<script>
import {clientError} from '@/intake/clientMessages'
import {bookingGuardRecovery} from '@/intake/bookingGuardRecovery'
import intakeFormMixin from '@/intake/intakeFormMixin'
import {displayTimeZone} from '@/intake/formatSlotDateTime'
import {
  loadIntake,
  saveIntake,
  hasSafetyRisk,
  isIntakeReadyToMatch,
  toFaDigits,
} from '@/intake/intakeState'
import {formatMoneyForDisplay} from '@/intake/formatMoney'
import {
  getMockMatch,
  MOCK_BOOKING_DATES,
  MOCK_BOOKING_TIMES,
} from '@/intake/mockMatches'
import {
  isSandboxMatchEnabled,
  getMemberSlots,
  selectMember,
  validateMatchBooking,
  isRecommendationNotActiveError,
  mapLiveMemberSlots,
  isStaleSlotOrQuoteFailure,
} from '@/intake/sandboxMatchApi'
import {
  getSessionPerson,
  hasSandboxMatchSession,
  loadMatchSession,
  saveMatchSession,
  clearMatchSession,
} from '@/intake/sandboxMatchSession'

export default {
  name: 'MatchSlotsStep',
  mixins: [intakeFormMixin],
  data() {
    const draft = loadIntake()
    return {
      person: null,
      dates: MOCK_BOOKING_DATES,
      times: MOCK_BOOKING_TIMES,
      sandboxSlots: [],
      sandboxMode: false,
      priceQuoteId: null,
      bookingDate: draft.bookingDate || '',
      bookingTime: draft.bookingTime || '',
      errors: {
        date: '',
        time: '',
      },
      formStatus: '',
      bookingGuard: null,
      submitting: false,
      rematchNeeded: false,
      slotsLoading: false,
      slotsError: false,
      timezone: displayTimeZone(),
    }
  },
  computed: {
    personName() {
      if (!this.person) {
        return ''
      }
      if (this.person.name) {
        return this.person.name
      }
      return this.$t('intake.therapist.' + this.person.id + '.name')
    },
    displaySessionPrice() {
      if (!this.person) {
        return ''
      }
      const money = formatMoneyForDisplay(this.person.price, this.person.currency)
      if (money.isToman || money.unit === 'raw') {
        return money.text + ' ' + this.$t('intake.toman')
      }
      if (money.currency) {
        return money.text + ' ' + money.currency
      }
      return money.text
    },
  },
  async created() {
    const draft = loadIntake()
    // V2 screening match: session people are authoritative — do not bounce to legacy draft gates.
    const hasV2Match = isSandboxMatchEnabled() && hasSandboxMatchSession()
    if (!hasV2Match) {
      if (!isIntakeReadyToMatch(draft)) {
        this.$router.replace({name: 'intakeScreening'})
        return
      }
      if (hasSafetyRisk(draft.safety)) {
        this.$router.replace({name: 'intakeCrisis'})
        return
      }
    }

    const id = this.$route.params.therapistId || draft.therapistId

    if (hasV2Match) {
      const person = getSessionPerson(id)
      if (!person) {
        this.$router.replace({name: 'intakeMatches'})
        return
      }
      this.person = person
      this.sandboxMode = true
      // Never reuse mock date ids (e.g. "d1") from a prior mock booking.
      this.bookingDate = ''
      this.bookingTime = ''
      saveIntake({therapistId: person.id, bookingDate: '', bookingTime: ''})
      await this.ensureSelectionThenLoadSlots(person)
      return
    }

    const person = getMockMatch(id)
    if (!person) {
      this.$router.replace({name: 'intakeMatches'})
      return
    }
    this.person = person
    saveIntake({therapistId: person.id})
  },
  methods: {
    progressPercent() { return '67%' },
    toFaDigits,
    progressMeta() {
      return this.$t('intake.progressMeta', {
        step: this.toFaDigits(2),
        total: this.toFaDigits(3),
      })
    },
    dateLabel(item) {
      return this.$i18n.locale === 'en' ? item.en : item.fa
    },
    onSandboxSlotChange() {
      this.errors.date = ''
      const slot = this.sandboxSlots.find((s) => s.slot_id === this.bookingDate)
      this.bookingTime = slot ? slot.starts_at : ''
    },
    selectedSandboxSlot() {
      return this.sandboxSlots.find((s) => s.slot_id === this.bookingDate) || null
    },
    async ensureSelectionThenLoadSlots(person) {
      if (this.slotsLoading) return
      this.slotsLoading = true
      this.slotsError = false
      this.formStatus = ''
      try {
        await this.ensureSelection(person)
        await this.loadSandboxSlots(person)
      } catch (e) {
        if (isRecommendationNotActiveError(e)) {
          clearMatchSession()
          this.rematchNeeded = true
          this.sandboxSlots = []
          this.formStatus = this.$t('intake.recommendationNotActive')
          return
        }
        this.slotsError = true
        this.formStatus = this.$t('intake.bookingUi.loadError')
        this.sandboxSlots = []
        this.priceQuoteId = null
      } finally {
        this.slotsLoading = false
      }
    },
    isUuid(value) {
      return /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
        .test(String(value || ''))
    },
    async ensureSelection(person) {
      const session = loadMatchSession()
      const canReuseSelection = this.isUuid(session.selectionId)
          && session.selectionMemberId === person.memberId
          && session.selectionRecommendationId === session.recommendationId
      const selectionId = canReuseSelection ? session.selectionId : this.newUuid()
      const selectionIdempotencyKey = canReuseSelection && session.selectionIdempotencyKey
          || ('select-' + selectionId).slice(0, 200)
      const selectedAt = canReuseSelection && session.selectionCreatedAt
          || new Date().toISOString()
      saveMatchSession({
        selectionId,
        selectionIdempotencyKey,
        selectionCreatedAt: selectedAt,
        selectionMemberId: person.memberId,
        selectionRecommendationId: session.recommendationId,
      })
      const selectRes = await selectMember(session.runId, {
        selection_id: selectionId,
        recommendation_id: session.recommendationId,
        recommendation_member_id: person.memberId,
        therapist_id: Number(person.id),
      }, {
        idempotencyKey: selectionIdempotencyKey,
      })
      saveMatchSession({
        selectionId: selectRes.selection_id || selectionId,
        selectionIdempotencyKey,
        selectionCreatedAt: selectedAt,
        selectionMemberId: person.memberId,
        selectionRecommendationId: session.recommendationId,
      })
      return selectRes.selection_id || selectionId
    },
    async loadSandboxSlots(person) {
      const session = loadMatchSession()
      try {
        const data = await getMemberSlots(session.recommendationId, person.memberId)
        const mapped = mapLiveMemberSlots(data, this.$route.params.lang || 'fa')
        this.sandboxSlots = mapped.slots
        this.priceQuoteId = mapped.priceQuoteId
        if (mapped.priceAmount != null && Number.isFinite(mapped.priceAmount)) {
          this.person = Object.assign({}, this.person, {
            price: mapped.priceAmount,
            currency: mapped.currency || this.person.currency,
          })
        }
      } catch (e) {
        if (isRecommendationNotActiveError(e)) {
          clearMatchSession()
          this.rematchNeeded = true
          this.sandboxSlots = []
          this.formStatus = this.$t('intake.recommendationNotActive')
          return
        }
        this.slotsError = true
        this.formStatus = this.$t('intake.bookingUi.loadError')
        // A live availability failure must not turn historical card data
        // into bookable-looking slots.
        this.sandboxSlots = []
        this.priceQuoteId = null
      }
      this.applyValidSandboxSelection()
    },
    newUuid() {
      if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return crypto.randomUUID()
      }
      return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = Math.random() * 16 | 0
        const v = c === 'x' ? r : (r & 0x3 | 0x8)
        return v.toString(16)
      })
    },
    applyValidSandboxSelection() {
      const ids = this.sandboxSlots.map((s) => s.slot_id)
      const session = loadMatchSession()
      const preferred = (session && session.selectedSlotId) || this.bookingDate
      if (preferred && ids.indexOf(preferred) !== -1) {
        this.bookingDate = preferred
        this.onSandboxSlotChange()
        return
      }
      this.bookingDate = ''
      this.bookingTime = ''
    },
    isSandboxSlotSelected() {
      return this.sandboxSlots.some((s) => s.slot_id === this.bookingDate)
    },
    goRematch() {
      clearMatchSession()
      this.$router.replace({
        name: 'intakeMatching',
        params: {lang: this.$route.params.lang || 'fa'},
      })
    },
    async submit() {
      if (this.submitting || this.bookingGuard) return
      this.formStatus = ''
      if (this.sandboxMode) {
        const nextErrors = {
          date: this.isSandboxSlotSelected() ? '' : this.$t('intake.fieldRequired'),
          time: '',
        }
        this.errors = nextErrors
        if (nextErrors.date) {
          this.formStatus = this.$t('intake.formIncomplete')
          this.noteInvalid()
          return
        }
        this.submitting = true
        try {
          const session = loadMatchSession()
          const selectionId = await this.ensureSelection(this.person)
          const validateIdempotencyKey = (
            'validate-'
            + String(selectionId)
            + '-'
            + String(this.bookingDate)
            + '-'
            + String(this.priceQuoteId || '')
          ).slice(0, 200)
          const validateRes = await validateMatchBooking({
            selection_id: selectionId,
            recommendation_id: session.recommendationId,
            recommendation_member_id: this.person.memberId,
            therapist_id: Number(this.person.id),
            slot_id: this.bookingDate,
            price_quote_id: this.priceQuoteId,
          }, {
            idempotencyKey: validateIdempotencyKey,
          })
          if (!validateRes.valid) {
            const recovery = bookingGuardRecovery(validateRes, this.$route.params.lang || 'fa')
            if (recovery) {
              this.bookingGuard = recovery
              this.formStatus = recovery.message
              this.submitting = false
              return
            }
            const codes = validateRes.reason_codes || []
            if (isStaleSlotOrQuoteFailure(codes)) {
              this.bookingDate = ''
              this.bookingTime = ''
              this.formStatus = this.$t('intake.slotOrQuoteStale')
              await this.loadSandboxSlots(this.person)
              this.submitting = false
              return
            }
            throw new Error(codes.join(', ') || 'INVALID')
          }
          const selectedSlot = this.selectedSandboxSlot()
          saveMatchSession({
            selectionId,
            validationId: validateRes.validation_id,
            priceQuoteId: this.priceQuoteId,
            holdId: null,
            holdExpiresAt: null,
            holdIdempotencyKey: null,
            selectedSlotId: this.bookingDate,
            selectedSlotStartsAt: selectedSlot && selectedSlot.starts_at,
            selectedSlotEndsAt: selectedSlot && selectedSlot.ends_at,
            selectedSlotLabel: (selectedSlot && selectedSlot.label) || this.bookingTime,
            selectedSlotDateLabel: (selectedSlot && selectedSlot.dateLabel) || '',
            selectedSlotTimeLabel: (selectedSlot && selectedSlot.timeLabel) || '',
          })
          saveIntake({
            therapistId: this.person.id,
            bookingDate: this.bookingDate,
            bookingTime: this.bookingTime || this.bookingDate,
          })
          this.$router.push({
            name: 'intakeCheckout',
            params: {lang: this.$route.params.lang || 'fa'},
          })
        } catch (e) {
          this.submitting = false
          const recovery = bookingGuardRecovery(e, this.$route.params.lang || 'fa')
          if (recovery) {
            this.bookingGuard = recovery
            this.formStatus = recovery.message
            return
          }
          if (isRecommendationNotActiveError(e)) {
            this.rematchNeeded = true
            this.formStatus = this.$t('intake.recommendationNotActive')
            clearMatchSession()
            return
          }
          this.formStatus = clientError(e, this.$route.params.lang || 'fa')
        }
        return
      }

      const nextErrors = {
        date: this.bookingDate ? '' : this.$t('intake.fieldRequired'),
        time: this.bookingTime ? '' : this.$t('intake.fieldRequired'),
      }
      this.errors = nextErrors
      if (nextErrors.date || nextErrors.time) {
        this.formStatus = this.$t('intake.formIncomplete')
        this.noteInvalid()
        return
      }

      this.submitting = true
      saveIntake({
        therapistId: this.person.id,
        bookingDate: this.bookingDate,
        bookingTime: this.bookingTime,
      })
      this.$router.push({
        name: 'intakeCheckout',
        params: {lang: this.$route.params.lang || 'fa'},
      })
    },
  },
}
</script>
